import type {
  AstNode,
  ChoiceNode,
  ChoiceOption,
  ExprNode,
  IfNode,
  ForNode,
  MacroNode,
  PixFile,
  TextSegment,
  ArgValue,
  CommandNode,
} from "./ast";
import { parseExpr } from "./expr";

/**
 * .pix ソースをパースして PixFile (AST) を返す。
 *
 * 戦略: 行単位の分類 → 構造化。Markdown 風の DSL は行ベースの構文がほとんど。
 */
export function parse(source: string, filename: string): PixFile {
  const lines = source.split(/\r?\n/);
  const lineTokens = lines.map((raw, i) => classifyLine(raw, i + 1));
  const nodes = buildAst(lineTokens);
  return { filename, nodes };
}

// =================================================================
// 行分類
// =================================================================

type LineToken =
  | { kind: "blank"; line: number }
  | { kind: "comment"; line: number }
  | { kind: "label"; line: number; name: string }
  | { kind: "narration"; line: number; text: string }
  | { kind: "speech"; line: number; speaker: string | null; text: string }
  | { kind: "command"; line: number; raw: string; name: string; rest: string }
  | { kind: "choice"; line: number; text: string; target: string; conditionRaw?: string }
  | { kind: "continuation"; line: number; text: string };

const RE_LABEL = /^##\s+([A-Za-z_][A-Za-z0-9_]*)\s*$/;
const RE_COMMENT = /^\s*(;;|\/\/)/;
const RE_NARRATION = /^>\s+(.*)$/;
const RE_SPEECH = /^([^@>;/\-\s][^:]*):\s+(.*)$/;
const RE_EMPTY_SPEAKER = /^:\s+(.*)$/; // `: text` で話者をクリア
const RE_COMMAND = /^\s*@([A-Za-z_][A-Za-z0-9_]*)\s*(.*)$/;
const RE_CHOICE = /^\s*-\s+\[(.+?)\]\(([^)]+)\)\s*(?:@if\s+(.+))?$/;

function classifyLine(raw: string, line: number): LineToken {
  const trimmed = raw.trim();

  if (trimmed === "") return { kind: "blank", line };
  if (RE_COMMENT.test(trimmed)) return { kind: "comment", line };

  let m: RegExpMatchArray | null;

  if ((m = trimmed.match(RE_LABEL))) {
    return { kind: "label", line, name: m[1]! };
  }
  if ((m = trimmed.match(RE_NARRATION))) {
    return { kind: "narration", line, text: stripTrailingComment(m[1]!) };
  }
  if ((m = trimmed.match(RE_COMMAND))) {
    return {
      kind: "command",
      line,
      raw: trimmed,
      name: m[1]!,
      rest: stripTrailingComment(m[2]!).trim(),
    };
  }
  if ((m = trimmed.match(RE_CHOICE))) {
    return {
      kind: "choice",
      line,
      text: m[1]!,
      target: m[2]!.trim(),
      conditionRaw: m[3]?.trim(),
    };
  }
  if ((m = trimmed.match(RE_EMPTY_SPEAKER))) {
    return { kind: "speech", line, speaker: null, text: stripTrailingComment(m[1]!) };
  }
  if ((m = trimmed.match(RE_SPEECH))) {
    return { kind: "speech", line, speaker: m[1]!.trim(), text: stripTrailingComment(m[2]!) };
  }
  // 上記いずれにも該当しない → 継続行
  return { kind: "continuation", line, text: stripTrailingComment(trimmed) };
}

function stripTrailingComment(s: string): string {
  // 文字列リテラル内の `;;` や `//` は無視する
  let inStr = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (c === '"' && s[i - 1] !== "\\") inStr = !inStr;
    if (!inStr) {
      if ((c === ";" && s[i + 1] === ";") || (c === "/" && s[i + 1] === "/")) {
        return s.slice(0, i).trimEnd();
      }
    }
  }
  return s;
}

// =================================================================
// AST 構築
// =================================================================

class AstBuilder {
  private idx = 0;
  constructor(private tokens: LineToken[]) {}

  build(endTokens: string[] = []): AstNode[] {
    const out: AstNode[] = [];
    while (this.idx < this.tokens.length) {
      const t = this.tokens[this.idx]!;

      // ブロック終端コマンドに当たったら抜ける
      if (t.kind === "command" && endTokens.includes(t.name)) {
        return out;
      }

      const node = this.readOne();
      if (node) out.push(node);
    }
    return out;
  }

  private readOne(): AstNode | null {
    const t = this.tokens[this.idx]!;
    switch (t.kind) {
      case "blank":
      case "comment":
        this.idx++;
        return null;
      case "label":
        this.idx++;
        return { type: "Label", name: t.name, line: t.line };
      case "speech":
        return this.readSpeech();
      case "narration":
        return this.readNarration();
      case "command":
        return this.readCommand();
      case "choice":
        return this.readChoiceBlock();
      case "continuation":
        // 単独継続行(直前が発話でない) → ナレーション扱い
        return this.readImplicitNarration();
    }
  }

  private readSpeech(): AstNode {
    const t = this.tokens[this.idx]! as Extract<LineToken, { kind: "speech" }>;
    this.idx++;
    const segments: TextSegment[] = parseInlineText(t.text);

    // 継続行を吸収(空行で区切る、別 speech/command/choice/label が来たら止まる)
    while (this.idx < this.tokens.length) {
      const next = this.tokens[this.idx]!;
      if (next.kind === "continuation") {
        segments.push({ kind: "text", value: "\n" });
        segments.push(...parseInlineText(next.text));
        this.idx++;
      } else {
        break;
      }
    }

    return {
      type: "Speech",
      speaker: t.speaker,
      segments,
      line: t.line,
    };
  }

  private readImplicitNarration(): AstNode {
    // 話者プレフィックスなしの単独行はナレーションとして扱う
    const t = this.tokens[this.idx]! as Extract<LineToken, { kind: "continuation" }>;
    this.idx++;
    const segments: TextSegment[] = parseInlineText(t.text);
    while (this.idx < this.tokens.length) {
      const next = this.tokens[this.idx]!;
      if (next.kind === "continuation") {
        segments.push({ kind: "text", value: "\n" });
        segments.push(...parseInlineText(next.text));
        this.idx++;
      } else {
        break;
      }
    }
    return {
      type: "Narration",
      segments,
      line: t.line,
    };
  }

  private readNarration(): AstNode {
    const t = this.tokens[this.idx]! as Extract<LineToken, { kind: "narration" }>;
    this.idx++;
    const segments: TextSegment[] = parseInlineText(t.text);
    while (this.idx < this.tokens.length) {
      const next = this.tokens[this.idx]!;
      if (next.kind === "narration") {
        // 連続するナレーションは continuation 扱い(段落として連結)
        // 空行のときは別メッセージ → break
        break;
      }
      if (next.kind === "continuation") {
        segments.push({ kind: "text", value: "\n" });
        segments.push(...parseInlineText(next.text));
        this.idx++;
      } else {
        break;
      }
    }
    return { type: "Narration", segments, line: t.line };
  }

  private readCommand(): AstNode {
    const t = this.tokens[this.idx]! as Extract<LineToken, { kind: "command" }>;
    this.idx++;

    switch (t.name) {
      case "if":
        return this.readIfBlock(t.rest, t.line);
      case "for":
        return this.readForBlock(t.rest, t.line);
      case "macro":
        return this.readMacroBlock(t.rest, t.line);
      default:
        return this.parseSingleCommand(t.name, t.rest, t.line);
    }
  }

  private readIfBlock(condRaw: string, line: number): IfNode {
    const branches: IfNode["branches"] = [];
    let elseBody: AstNode[] | undefined;

    const firstCond = parseExpr(condRaw);
    const firstBody = this.build(["elseif", "else", "endif"]);
    branches.push({ condition: firstCond, body: firstBody });

    while (this.idx < this.tokens.length) {
      const t = this.tokens[this.idx]!;
      if (t.kind === "command" && t.name === "elseif") {
        this.idx++;
        const cond = parseExpr(t.rest);
        const body = this.build(["elseif", "else", "endif"]);
        branches.push({ condition: cond, body });
      } else if (t.kind === "command" && t.name === "else") {
        this.idx++;
        elseBody = this.build(["endif"]);
      } else if (t.kind === "command" && t.name === "endif") {
        this.idx++;
        break;
      } else {
        throw new Error(`@endif が必要です(行 ${line} の @if に対して)`);
      }
    }
    return { type: "If", branches, elseBody, line };
  }

  private readForBlock(rest: string, line: number): ForNode {
    // 形式: `i = 0 to 100 [step 2]`
    const m = rest.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.+?)\s+to\s+(.+?)(?:\s+step\s+(.+))?$/);
    if (!m) throw new Error(`@for の構文エラー(行 ${line}): '${rest}'`);
    const node: ForNode = {
      type: "For",
      varName: m[1]!,
      start: parseExpr(m[2]!),
      end: parseExpr(m[3]!),
      step: m[4] ? parseExpr(m[4]) : undefined,
      body: this.build(["endfor"]),
      line,
    };
    if (this.idx >= this.tokens.length || (this.tokens[this.idx] as LineToken).kind !== "command") {
      throw new Error(`@endfor が必要です(行 ${line} の @for に対して)`);
    }
    this.idx++; // consume @endfor
    return node;
  }

  private readMacroBlock(rest: string, line: number): MacroNode {
    const m = rest.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*$/);
    if (!m) throw new Error(`@macro の構文エラー(行 ${line}): '${rest}'`);
    const body = this.build(["endmacro"]);
    if (this.idx >= this.tokens.length) {
      throw new Error(`@endmacro が必要です(行 ${line} の @macro に対して)`);
    }
    this.idx++;
    return { type: "Macro", name: m[1]!, body, line };
  }

  private parseSingleCommand(name: string, rest: string, line: number): CommandNode {
    // 引数形式は 3 系統あるが、簡略のためここでは key=value のパースのみ。
    // 式型コマンド(@set, @jump 等)は実行時にコンパイラが式評価する。
    // コンパイラはこの args / subcommand / rest 全部を見てよろしくやる(下記注釈)。
    //
    // 戦略: 残り文字列を「サブコマンド + key=value...」または「式」として両用に扱う。
    // - サブコマンド: 識別子1個が先頭にあれば extract
    // - key=value: 残りを kvParse
    // - 式型コマンド(set/jump/call): コンパイラが args._raw を見て解釈

    const argsRecord: Record<string, ArgValue> = {};
    argsRecord["_raw"] = rest; // raw を保持(式型コマンドのため)

    let subcommand: string | undefined;
    let work = rest;

    // 先頭の識別子があり、その後ろが空白 + key=value... なら subcommand 扱い
    const subMatch = work.match(/^([A-Za-z_][A-Za-z0-9_]*)(\s+|$)/);
    if (subMatch && (work[subMatch[0].length] !== "=" || subMatch[2] !== "")) {
      // 続きを見る — `name=value` 形式が来てなければ subcommand
      const after = work.slice(subMatch[0].length).trim();
      if (after === "" || /^[A-Za-z_][A-Za-z0-9_]*\s*=/.test(after)) {
        subcommand = subMatch[1]!;
        work = after;
      }
    }

    // key=value のパース
    parseKvPairs(work, argsRecord);

    return {
      type: "Command",
      name,
      subcommand,
      args: argsRecord,
      line,
    };
  }

  private readChoiceBlock(): ChoiceNode {
    const first = this.tokens[this.idx]! as Extract<LineToken, { kind: "choice" }>;
    const options: ChoiceOption[] = [];
    while (this.idx < this.tokens.length) {
      const t = this.tokens[this.idx]!;
      if (t.kind !== "choice") break;
      const targetParsed = parseTarget(t.target);
      const condition: ExprNode | undefined = t.conditionRaw ? parseExpr(t.conditionRaw) : undefined;
      options.push({
        text: parseInlineText(t.text),
        targetFile: targetParsed.file,
        targetLabel: targetParsed.label,
        condition,
      });
      this.idx++;
    }
    return { type: "Choice", options, line: first.line };
  }
}

function buildAst(tokens: LineToken[]): AstNode[] {
  return new AstBuilder(tokens).build();
}

// =================================================================
// 補助関数
// =================================================================

function parseTarget(s: string): { file?: string; label: string } {
  // 形式: `#label` または `path#label`
  const hashIdx = s.indexOf("#");
  if (hashIdx < 0) {
    return { label: s.trim() };
  }
  const file = s.slice(0, hashIdx).trim();
  const label = s.slice(hashIdx + 1).trim();
  return file === "" ? { label } : { file, label };
}

// インラインタグを含むテキストをセグメント列にパース
function parseInlineText(s: string): TextSegment[] {
  const out: TextSegment[] = [];
  let i = 0;
  let buf = "";
  while (i < s.length) {
    const c = s[i]!;
    if (c === "[") {
      // タグ開始 - ただし `[テキスト](target)` は選択肢で処理済みなのでここに来ないはず
      const closeIdx = findInlineTagClose(s, i);
      if (closeIdx < 0) {
        buf += c;
        i++;
        continue;
      }
      if (buf.length > 0) {
        out.push({ kind: "text", value: buf });
        buf = "";
      }
      const inside = s.slice(i + 1, closeIdx);
      const seg = parseInlineTag(inside);
      out.push(seg);
      i = closeIdx + 1;
    } else {
      buf += c;
      i++;
    }
  }
  if (buf.length > 0) {
    out.push({ kind: "text", value: buf });
  }
  return out;
}

function findInlineTagClose(s: string, start: number): number {
  // start は '[' の位置
  let depth = 0;
  let inStr = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i]!;
    if (c === '"' && s[i - 1] !== "\\") inStr = !inStr;
    if (inStr) continue;
    if (c === "[") depth++;
    else if (c === "]") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function parseInlineTag(inside: string): TextSegment {
  // 形式:
  //   `tagname attr=value attr=value`
  //   `tagname value1 value2 ...` (短縮形 — Phase 1 ではキーつきだけサポート)
  //   `/tagname` (閉じタグ)
  //   `tagname=value` (color/size のショート — 第一属性が value)
  const trimmed = inside.trim();
  if (trimmed.startsWith("/")) {
    const name = trimmed.slice(1).trim();
    return { kind: "tag", name, attrs: {}, closing: true, selfClose: false };
  }
  // `color=#FF0000` のような糖衣を許可: 先頭が `name=value`
  const shortMatch = trimmed.match(/^([A-Za-z_][A-Za-z0-9_]*)=(.+?)(\s+.+)?$/);
  if (shortMatch && !trimmed.match(/^[A-Za-z_][A-Za-z0-9_]*\s/)) {
    const name = shortMatch[1]!;
    const attrs: Record<string, string> = { value: shortMatch[2]! };
    if (shortMatch[3]) {
      parseKvPairs(shortMatch[3].trim(), attrs);
    }
    return { kind: "tag", name, attrs, closing: false, selfClose: true };
  }

  // `tagname [args...]`
  const m = trimmed.match(/^([A-Za-z_][A-Za-z0-9_]*)(?:\s+(.*))?$/);
  if (!m) return { kind: "text", value: `[${inside}]` } as never;
  const name = m[1]!;
  const argsRaw = m[2] ?? "";
  const attrs: Record<string, string> = {};
  parseKvPairs(argsRaw, attrs);
  return { kind: "tag", name, attrs, closing: false, selfClose: true };
}

/**
 * `key=value` / フラグ(識別子単体) / 位置引数(`_arg0`, `_arg1`...) を混在で受け付ける。
 *
 * 例:
 *   `name=street fade=1.0`              → { name: "street", fade: 1.0 }
 *   `何処 どこ`                          → { _arg0: "何処", _arg1: "どこ" }
 *   `time=200`                           → { time: 200 }
 *   `loop`                               → { loop: true }
 *   `text="hello world" id=alice`        → { text: "hello world", id: "alice" }
 */
export function parseKvPairs(src: string, out: Record<string, ArgValue | string>): void {
  let i = 0;
  let posIdx = 0;
  while (i < src.length) {
    // 空白スキップ
    while (i < src.length && /\s/.test(src[i]!)) i++;
    if (i >= src.length) break;

    // 1 トークン読み取り(クォート内のスペースは無視)
    const tokenStart = i;
    let inQuotes = false;
    while (i < src.length) {
      const c = src[i]!;
      if (c === '"' && src[i - 1] !== "\\") inQuotes = !inQuotes;
      if (!inQuotes && /\s/.test(c)) break;
      i++;
    }
    const token = src.slice(tokenStart, i);
    if (token === "") {
      i++;
      continue;
    }

    // `key=value` 形式?
    const eqIdx = findUnquotedEqual(token);
    if (eqIdx > 0) {
      const keyPart = token.slice(0, eqIdx);
      if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(keyPart)) {
        let valPart = token.slice(eqIdx + 1);
        valPart = stripOuterQuotes(unescapeStr(valPart));
        out[keyPart] = coerceArgValue(valPart);
        continue;
      }
    }

    // 識別子単体 → フラグ
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(token)) {
      out[token] = true;
      continue;
    }

    // それ以外 → 位置引数
    out[`_arg${posIdx++}`] = stripOuterQuotes(unescapeStr(token));
  }
}

function findUnquotedEqual(s: string): number {
  let inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (c === '"' && s[i - 1] !== "\\") inQuotes = !inQuotes;
    if (!inQuotes && c === "=") return i;
  }
  return -1;
}

function stripOuterQuotes(s: string): string {
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    return s.slice(1, -1);
  }
  return s;
}

function unescapeStr(s: string): string {
  return s.replace(/\\(["\\nrt])/g, (_, c) => {
    switch (c) {
      case "n":
        return "\n";
      case "r":
        return "\r";
      case "t":
        return "\t";
      default:
        return c;
    }
  });
}

function coerceArgValue(s: string): ArgValue {
  if (s === "true") return true;
  if (s === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(s)) return parseFloat(s);
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s; // hex color stays as string
  return s;
}
